import React from "react";
import { useTranslation } from "react-i18next";

const LANGUAGES = [
    { code: "en", label: "English", title: "Available in your language" },
    { code: "es", label: "Español", title: "Disponible en tu idioma" },
    { code: "fr", label: "Français", title: "Disponible dans votre langue" },
    { code: "de", label: "Deutsch", title: "Verfügbar in Ihrer Sprache" },
    { code: "it", label: "Italiano", title: "Disponibile nella tua lingua" },
    { code: "pt", label: "Português", title: "Disponível no seu idioma" },
    { code: "ar", label: "العربية", title: "متاح بلغتك" },
    { code: "hi", label: "हिन्दी", title: "आपकी भाषा में उपलब्ध" },
    { code: "tr", label: "Türkçe", title: "Sizin dilinizde mevcut" },
];

const RTL_CODES = new Set(["ar"]);

export default function AuthLanguagePicker() {
    const { t, i18n } = useTranslation();
    const current = (i18n.language || "en").split("-")[0];
    const [rotateIndex, setRotateIndex] = React.useState(0);

    React.useEffect(() => {
        const reduced =
            typeof window !== "undefined" &&
            window.matchMedia &&
            window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        if (reduced) return;
        const id = setInterval(() => {
            setRotateIndex((i) => (i + 1) % LANGUAGES.length);
        }, 2200);
        return () => clearInterval(id);
    }, []);

    const change = (code) => {
        if (code === current) return;
        i18n.changeLanguage(code);
        try {
            localStorage.setItem("i18nextLng", code);
        } catch {
            // ignore
        }
    };

    const rotating = LANGUAGES[rotateIndex];
    const rotatingDir = RTL_CODES.has(rotating.code) ? "rtl" : "ltr";

    return (
        <section
            className="mt-12 md:mt-16 text-center md:relative md:left-1/2 md:-translate-x-1/2 md:w-screen md:px-4"
            aria-label={t("auth.languagePicker.title")}
        >
            <style>{`
                @keyframes authLangFade {
                    0% { opacity: 0; transform: translateY(6px); }
                    20% { opacity: 1; transform: translateY(0); }
                    80% { opacity: 1; transform: translateY(0); }
                    100% { opacity: 0; transform: translateY(-6px); }
                }
                @media (prefers-reduced-motion: reduce) {
                    .auth-lang-rotate { animation: none !important; }
                }
            `}</style>
            <h2
                className="font-display md:whitespace-nowrap text-[clamp(28px,6vw,60px)] leading-[1.1] tracking-[-0.025em] font-medium text-[#171a1f] mb-7 px-2"
                aria-hidden="true"
            >
                <span
                    key={rotating.code}
                    lang={rotating.code}
                    dir={rotatingDir}
                    className="auth-lang-rotate inline-block"
                    style={{ animation: "authLangFade 2200ms ease-in-out both" }}
                >
                    {rotating.title}
                </span>
            </h2>
            <div className="flex flex-wrap justify-center gap-2">
                {LANGUAGES.map(({ code, label }) => {
                    const active = code === current;
                    return (
                        <button
                            key={code}
                            type="button"
                            onClick={() => change(code)}
                            aria-pressed={active}
                            lang={code}
                            className={`inline-flex items-center rounded-full border py-1.5 px-3.5 text-[12.5px] transition-colors focus:outline-none focus-visible:ring-[3px] focus-visible:ring-[#a594c8]/30 ${
                                active
                                    ? "bg-[#0a0d12] border-[#0a0d12] text-white"
                                    : "bg-white border-[#f1eff5] text-[#6b7280] hover:border-[#d8d4e0] hover:text-[#171a1f]"
                            }`}
                        >
                            {label}
                        </button>
                    );
                })}
            </div>
        </section>
    );
}
